import { useQuery } from '@tanstack/react-query';

import { QueryKey } from '@/config/query-client';
import { isIdNotRecognizedError } from '@/helpers/is-id-not-recognized-error';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { useEffect, useMemo } from 'react';
import { fetchCompany } from '@/api/v4/organization.api';
import { fetchSpotlightCompany } from '@/api/v4/spotlight.api';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from './company-fetch-context';

const companyFetchers: Record<
  CompanyFetchMode,
  (companyId: string) => ReturnType<typeof fetchCompany>
> = {
  general: fetchCompany,
  spotlight: fetchSpotlightCompany,
};

export const useCompany = (companyId: string | null) => {
  const { hasAtLeastOnePermission } = useUserPermissions();
  const navigateTo = useNavigateTo();
  const fetchMode = useCompanyFetchMode();

  const getCompany = () => {
    if (!companyId) return;
    return companyFetchers[fetchMode](companyId);
  };

  const query = useQuery({
    queryKey: [QueryKey.Company, fetchMode, companyId],
    queryFn: getCompany,
    retry: false,
    enabled:
      !!companyId &&
      hasAtLeastOnePermission([PERMISSION.VIEW_ORGANIZATION_HOME_SCREEN]),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (query.error && isIdNotRecognizedError(query.error)) {
      navigateTo.notFound();
    }
  }, [query.error, navigateTo]);

  const isCrunchbaseCompany = useMemo(
    () => query.isFetched && !!query.data?.crunchbasePermalink,
    [query.data?.crunchbasePermalink, query.isFetched],
  );

  return { ...query, isCrunchbaseCompany };
};
